import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  fetchKargoFirmalari,
  createKargoFirmasi,
  updateKargoFirmasi,
  deleteKargoFirmasi,
  reorderKargoFirmalari,
} from '../services/kargoFirmasiService';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import './BirimYonetimiModal.css';
import './KargoFirmasiYonetimi.css';

const emptyForm = () => ({ ad: '' });

const rowId = (k) => String(k.id || k._id);

function SortableKargoRow({ firma, onEdit, onDelete }) {
  const id = rowId(firma);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
  };

  return (
    <tr ref={setNodeRef} style={style} className={isDragging ? 'birim-row--dragging' : ''}>
      <td className="birim-drag-cell">
        <button
          type="button"
          className="birim-drag-handle"
          {...attributes}
          {...listeners}
          aria-label="Sırayı değiştirmek için sürükleyin"
          title="Sırayı değiştirmek için sürükleyin"
        >
          <i className="fas fa-grip-vertical" aria-hidden="true" />
        </button>
      </td>
      <td>{firma.ad}</td>
      <td className="table-actions islemler-sutunu">
        <button
          type="button"
          className="category-action-btn edit-btn"
          onClick={() => onEdit(firma)}
          title="Düzenle"
        >
          <i className="fas fa-edit" aria-hidden="true" />
        </button>
        <button
          type="button"
          className="category-action-btn delete-btn"
          onClick={() => onDelete(firma)}
          title="Sil"
        >
          <i className="fas fa-trash-alt" aria-hidden="true" />
        </button>
      </td>
    </tr>
  );
}

export default function KargoFirmasiYonetimi({ open = true, onClose = () => {}, onSaved, variant = 'embedded' }) {
  const { confirm } = useConfirmDialog();
  const embedded = variant === 'embedded';
  const [liste, setListe] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const sortableIds = useMemo(() => liste.map((k) => rowId(k)), [liste]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetchKargoFirmalari();
      const arr = r?.veri?.kargoFirmalari || [];
      setListe(Array.isArray(arr) ? arr : []);
    } catch (e) {
      setError(e?.message || 'Kargo firmaları yüklenemedi');
      setListe([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    load();
    setEditingId(null);
    setForm(emptyForm());
  }, [open, load]);

  const startEdit = (k) => {
    setEditingId(k.id || k._id);
    setForm({ ad: k.ad || '' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ad: form.ad.trim() };
      if (!payload.ad) {
        setError('Firma adı zorunludur.');
        setSaving(false);
        return;
      }
      if (editingId) {
        const r = await updateKargoFirmasi(editingId, payload);
        if (!r?.basarili) throw new Error(r?.mesaj || 'Güncellenemedi');
      } else {
        const r = await createKargoFirmasi(payload);
        if (!r?.basarili) throw new Error(r?.mesaj || 'Oluşturulamadı');
      }
      if (window.showNotification) {
        window.showNotification('success', editingId ? 'Kargo firması güncellendi' : 'Kargo firması eklendi');
      }
      cancelEdit();
      await load();
      if (onSaved) onSaved();
    } catch (err) {
      setError(err?.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (k) => {
    const id = k.id || k._id;
    if (!id) return;
    const ok = await confirm({
      title: 'Kargo firmasını sil',
      message: `"${k.ad}" firmasını silmek istediğinize emin misiniz?`,
      variant: 'danger',
      confirmLabel: 'Evet, sil',
    });
    if (!ok) return;
    setError('');
    try {
      const r = await deleteKargoFirmasi(id);
      if (!r?.basarili) throw new Error(r?.mesaj || 'Silinemedi');
      if (window.showNotification) window.showNotification('success', 'Kargo firması silindi');
      if (editingId === id) cancelEdit();
      await load();
      if (onSaved) onSaved();
    } catch (err) {
      setError(err?.message || 'Silinemedi');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || reordering) return;

    const oldIndex = liste.findIndex((k) => rowId(k) === String(active.id));
    const newIndex = liste.findIndex((k) => rowId(k) === String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(liste, oldIndex, newIndex);
    const prevListe = liste;
    setListe(reordered);
    setReordering(true);
    setError('');

    try {
      const r = await reorderKargoFirmalari(reordered.map((k) => rowId(k)));
      if (!r?.basarili) throw new Error(r?.mesaj || 'Sıra güncellenemedi');
      const arr = r?.veri?.kargoFirmalari;
      if (Array.isArray(arr)) setListe(arr);
      if (window.showNotification) window.showNotification('success', 'Sıra güncellendi');
      if (onSaved) onSaved();
    } catch (err) {
      setListe(prevListe);
      setError(err?.message || 'Sıra güncellenemedi');
      if (window.showNotification) window.showNotification('error', err?.message || 'Sıra güncellenemedi');
    } finally {
      setReordering(false);
    }
  };

  if (!open) return null;

  const formBlock = (
    <>
      {embedded ? (
        <div style={{ marginBottom: 16 }}>
          <h2 id="kargo-firmasi-embedded-title" style={{ margin: 0, fontSize: '1.2rem', fontWeight: 600, color: 'var(--text-dark)' }}>
            Kargo firmaları
          </h2>
          <p style={{ margin: '8px 0 0', fontSize: 12, lineHeight: 1.5, color: 'var(--text-gray)' }}>
            Kargo giriş formlarında seçilecek firmalar. Sıralamak için satırdaki tutamacı sürükleyin.
          </p>
        </div>
      ) : (
        <div className="modal-header">
          <h2 id="kargo-firmasi-modal-title">Kargo firmaları</h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>
      )}

      <form
        className={embedded ? 'birim-yonetimi-embedded__form' : 'content-item-form-modal__form'}
        onSubmit={handleSubmit}
      >
        <div className={embedded ? 'birim-yonetimi-embedded__scroll' : 'content-item-form-modal__scroll'}>
          <label className="form-field">
            <span>Firma adı *</span>
            <input
              type="text"
              value={form.ad}
              onChange={(e) => setForm((p) => ({ ...p, ad: e.target.value }))}
              maxLength={100}
              placeholder="Örn. Aras Kargo"
            />
          </label>

          {error && (
            <div className="form-error" style={{ marginTop: 12 }}>
              <i className="fas fa-exclamation-circle" aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}

          <div className="form-group" style={{ marginTop: 20 }}>
            <label>Firma listesi</label>
            {reordering ? (
              <p className="birim-reorder-hint" role="status">
                <i className="fas fa-spinner fa-spin" aria-hidden="true" /> Sıra kaydediliyor…
              </p>
            ) : null}
            {loading ? (
              <div className="loading-state" style={{ padding: '24px 0' }}>
                <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                <span>Yükleniyor…</span>
              </div>
            ) : (
              <div className="table-container">
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                  <table className="admins-table birim-sortable-table kargo-firmasi-table">
                    <thead>
                      <tr>
                        <th className="birim-drag-cell" aria-label="Sıra" />
                        <th>Firma adı</th>
                        <th className="islemler-sutunu">İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                        {liste.map((k) => (
                          <SortableKargoRow
                            key={rowId(k)}
                            firma={k}
                            onEdit={startEdit}
                            onDelete={handleDelete}
                          />
                        ))}
                      </SortableContext>
                    </tbody>
                  </table>
                </DndContext>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          {!embedded && (
            <button type="button" className="secondary-btn" onClick={onClose}>
              İptal
            </button>
          )}
          {editingId && (
            <button type="button" className="secondary-btn" onClick={cancelEdit}>
              Yeni firma
            </button>
          )}
          <button type="submit" className="primary-btn" disabled={saving || reordering}>
            {saving ? (
              <>
                <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                <span>Kaydediliyor…</span>
              </>
            ) : (
              <>
                <i className="fas fa-save" aria-hidden="true" />
                <span>{editingId ? 'Güncelle' : 'Ekle'}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </>
  );

  if (embedded) {
    return (
      <div className="card birim-yonetimi-embedded kargo-firmasi-yonetimi">
        {formBlock}
      </div>
    );
  }

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" onClick={onClose}>
      <div
        className="modal-content content-item-form-modal birim-yonetimi-modal kargo-firmasi-yonetimi-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {formBlock}
      </div>
    </div>
  );
}
